// Prod: pay.cakto.com.br/{slug} — a borda já encaminha a raiz pro checkout v2.
// Staging: o v2 não tem essa borda e só responde sob /checkout/{slug}; o v1
// (checkout-stg.cakto.me) não é mais o checkout que o produtor deve abrir.
const STAGING_CHECKOUT_BASE = 'https://checkout-v2-stg.cakto.com.br/checkout';
const PROD_CHECKOUT_BASE = 'https://pay.cakto.com.br';

function normalizeBaseUrl(url) {
  return String(url || '').replace(/\/+$/, '');
}

export function isStagingApiUrl(apiUrl = '') {
  return /stg|stag|hml|homolog/i.test(apiUrl);
}

function isStagingCheckoutUrl(checkoutUrl = '') {
  return /checkout-stg\.cakto\.me|paystg|checkoutstg|stg\.cakto/i.test(checkoutUrl);
}

/**
 * Base URL do checkout alinhada ao ambiente da API.
 * Em staging é sempre o v2: o REACT_APP_CHECKOUT_URL do deploy de staging ainda
 * aponta pro v1. Em prod, REACT_APP_CHECKOUT_URL só vale se não for de staging.
 */
export function resolveCheckoutBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (isStagingApiUrl(apiUrl)) return STAGING_CHECKOUT_BASE;

  const explicit = normalizeBaseUrl(process.env.REACT_APP_CHECKOUT_URL);
  if (!explicit || isStagingCheckoutUrl(explicit)) return PROD_CHECKOUT_BASE;

  return explicit;
}

// A API devolve tanto /{slug} (v1) quanto /checkout/{slug} (v2), em qualquer host;
// o slug é extraído e remontado sobre a base do ambiente.
export function normalizeCheckoutUrlEnv(url) {
  if (!url || typeof url !== 'string') return url;

  try {
    const parsed = new URL(url);
    const slugPath = parsed.pathname.replace(/^\/checkout(?=\/|$)/i, '');

    return `${resolveCheckoutBaseUrl()}${slugPath}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/**
 * O checkout lê o caminho só como id de oferta, com o do checkout opcional depois do `_`
 * (`/api/product/checkout/{offer_checkout_id}/` no backend). Não existe checkout de produto: o
 * short_id do produto nesse lugar é 404, e o `?offer=` não chega ao backend (CKTMF-481).
 */
export function buildCheckoutUrl(offerCheckoutId) {
  const normalizedId =
    offerCheckoutId != null ? String(offerCheckoutId).trim().replace(/^\/+/, '') : '';
  if (!normalizedId) return null;

  const base = resolveCheckoutBaseUrl();
  if (!base) return null;

  return `${base}/${encodeURIComponent(normalizedId)}`;
}

export function buildLinksByOfferId(links = []) {
  const map = {};

  links.forEach((link) => {
    const offerId = link?.offer?.id;
    if (offerId && link?.url) {
      map[offerId] = link.url;
    }
  });

  return map;
}

export function getPrimaryOffer(product) {
  const offers = Array.isArray(product?.offers) ? product.offers : [];
  return offers.find((offer) => offer?.default) || offers[0] || null;
}

/**
 * Resolve checkout URL de uma oferta (mesma prioridade da lista de ofertas).
 */
export function resolveOfferCheckoutUrl(offer, product, linksByOfferId = {}) {
  const raw = offer?._raw ?? offer;
  const offerId = raw?.id ?? raw?.offerId;

  if (offerId && linksByOfferId[offerId]) {
    return normalizeCheckoutUrlEnv(linksByOfferId[offerId]);
  }

  if (raw?.checkout_url || raw?.url) {
    return normalizeCheckoutUrlEnv(raw.checkout_url || raw.url);
  }

  return buildCheckoutUrl(offerId);
}

/**
 * URL de checkout da oferta principal do produto (link cadastrado > fallback montado).
 */
export function getProductCheckoutUrl(product, { linksByOfferId = {} } = {}) {
  if (!product) return null;

  const mainOffer = getPrimaryOffer(product);
  if (!mainOffer) return null;

  return resolveOfferCheckoutUrl(mainOffer, product, linksByOfferId);
}
